import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getpackage } from "../../store/slices/package";
import CopyPackageStep from "./copyPackageStep";
import CopyPackageStepper from "./copyPackageStepper";
import CopyFerryStep from "./copyferrystep";
import CopyHotelStep from "./copyhotelstep";
import CopyActivityStep from "./copyactivitystep";
import CopyCarStep from "./copycarStep";
import CopyDayPlanStep from "./copydayPlanStep";

import PricingStep from "./pricingStep";
import CopyOptionStep from "./copyoptionStep";

const steps = [
  "Package",
  "Options",
  "Itinerary",
  "Hotel",
  "Activity",
  "Car",
  "Ferry",
  "Pricing",
];

export default function CopyPackages() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const hasFetched = useRef(false);

  // ---------------- STATE ----------------
  const [currentStep, setCurrentStep] = useState(0);
  const [packageId, setPackageId] = useState(null);
  const [durationNights, setDurationNights] = useState(null);
  const [durationDays, setdurationDays] = useState(null);
  const [isPackageLoaded, setIsPackageLoaded] = useState(false);
  const [packageForm, setPackageForm] = useState({
    title: "",
    places: [],
    category: "",
    durationDays: "",
    durationNights: "",
    pricingType: "dynamic",
    basePax: 2,
    description: "",
    inclusions: "",
    exclusions: "",
    paymentpolicy: "",
    image: [],
  });

  const [packageOptions, setPackageOptions] = useState([]);
  const [packageDays, setPackageDays] = useState([]);
  const [packageHotels, setPackageHotels] = useState([]);
  const [packageActivities, setPackageActivities] = useState([]);
  const [packageCars, setPackageCars] = useState([]);
  const [packageFerries, setPackageFerries] = useState([]);

  // ---------------- FETCH EDIT DATA ----------------
  useEffect(() => {
    if (!id) return;
    if (hasFetched.current) return;

    hasFetched.current = true;

    const fetchPackage = async () => {
      try {
        const res = await dispatch(getpackage({ id })).unwrap();
        const data = res?.data;

        if (!data) return;
        setPackageId(null);
        setDurationNights(data.durationNights);
        setdurationDays(data.durationDays);

        setPackageForm({
          title: data.title || "",
          places: data.places?.map((p) => p._id) || [],
          category: data.category?._id || "",
          durationDays: data.durationDays || "",
          durationNights: data.durationNights || "",
          pricingType: data.pricingType || "dynamic",
          basePax: data.basePax || 2,
          description: data.description || "",
          inclusions: data.inclusions || "",
          exclusions: data.exclusions || "",
          paymentpolicy: data.paymentpolicy || "",
          image: data.image || [],
        });

        setIsPackageLoaded(true);
        setPackageOptions(
          (data.options || []).map(({ _id, ...rest }) => rest)
        );

        setPackageDays(
          (data.dayPlans || []).map(({ _id, ...rest }) => rest)
        );
        setPackageHotels(
          (data.hotels || []).map((h) => ({
            _id: undefined,
            packageOption: h.packageOption?._id || "",
            dayPlan: h.dayPlan?._id || "",
            place: h.hotel?.location?._id || "",
            hotel: h.hotel?._id || "",
            roomType: h.roomType?._id || "",

            nights: h.nights || 1,
            rooms: h.rooms || 1,
            extraBeds: h.extraBeds || 0,

            roomPrice: h.roomPrice ?? h.roomType?.basePrice ?? 0,
            extraBedPrice: h.extraBedPrice ?? h.roomType?.extraBedPrice ?? 0,

            meals: h.meals || {
              breakfast: false,
              lunch: false,
              dinner: false,
            },
          })),
        );
        setPackageActivities(
          (data.activities || []).map((a) => ({
            _id: undefined,
            packageOption: a.packageOption?._id || "",
            dayPlan: a.dayPlan?._id || "",
            activities:
              a.activities?.map((act) => ({
                _id: undefined,
                activity: act.activity?._id || "",
                vendor: act.vendor?._id || "",
                timing: act.timing || "",
              })) || [],
          })),
        );

        setPackageCars(
          (data.vehicles || []).map((c) => ({
            _id: undefined,
            packageOption: c.packageOption?._id,
            dayPlan: c.dayPlan?._id,
            vehicleType: c.vehicleType?._id || "",
            pricePerDay: c?.pricePerDay,
          })),
        );
        setPackageFerries(
          (data.ferrys || []).map((doc) => ({
            _id: undefined,

            packageOption: doc.packageOption?._id,
            packageOptionOrder: doc.packageOption?.displayOrder,

            dayPlan: doc.dayPlan?._id,
            dayNumber: doc.dayPlan?.dayNumber,
            ferries:
              doc.ferries?.map((f) => ({
                _id: undefined,
                ferry: "", // UI index
                ferryId: f.ferry?._id, // IMPORTANT
                fromPlace: f.fromPlace?._id || "",
                toPlace: f.toPlace?._id || "",
                scheduleIndex: f.scheduleIndex ?? "",
                className: f.className || "",
                quantity: f.quantity || 1,
              })) || [],
          })),
        );
      } catch (err) {
        console.error("Failed to fetch package:", err);
      }
    };

    fetchPackage();
  }, [id, dispatch]);

  // ---------------- UI ----------------
  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>

          <Link to="/admin/packages" className="breadcrumb-link active">
            Packages
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span className="breadcrumb-link inactive">
            Copy Package
          </span>
        </div>
      </div>

      <CopyPackageStepper
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        isEditable={false}
      />
      {currentStep === 0 && isPackageLoaded && (
        <CopyPackageStep
          form={packageForm}
          setForm={setPackageForm}
          packageId={packageId}
          setPackageId={setPackageId}
          setDurationDays={setdurationDays}
          nextStep={() => setCurrentStep(1)}
        />
      )}
      {currentStep === 1 && (
        <CopyOptionStep
          packageId={packageId}
          options={packageOptions}
          setOptions={setPackageOptions}
          nextStep={() => setCurrentStep(2)}
          prevStep={() => setCurrentStep(0)}
        />
      )}

      {currentStep === 2 && (
        <CopyDayPlanStep
          packageId={packageId}
          durationDays={durationDays}
          days={packageDays}
          setDays={setPackageDays}
          nextStep={() => setCurrentStep(3)}
          prevStep={() => setCurrentStep(1)}
        />
      )}

      {currentStep === 3 && (
        <CopyHotelStep
          packageId={packageId}
          hotels={packageHotels}
          setHotels={setPackageHotels}
          nextStep={() => setCurrentStep(4)}
          prevStep={() => setCurrentStep(2)}
        />
      )}

      {currentStep === 4 && (
        <CopyActivityStep
          packageId={packageId}
          activities={packageActivities}
          setActivities={setPackageActivities}
          nextStep={() => setCurrentStep(5)}
          prevStep={() => setCurrentStep(3)}
        />
      )}

      {currentStep === 5 && (
        <CopyCarStep
          packageId={packageId}
          cars={packageCars}
          setCars={setPackageCars}
          nextStep={() => setCurrentStep(6)}
          prevStep={() => setCurrentStep(4)}
        />
      )}

      {currentStep === 6 && (
        <CopyFerryStep
          packageId={packageId}
          ferries={packageFerries}
          setFerries={setPackageFerries}
          nextStep={() => setCurrentStep(7)}
          prevStep={() => setCurrentStep(5)}
        />
      )}

      {currentStep === 7 && (
        <PricingStep packageId={packageId} prevStep={() => setCurrentStep(6)} />
      )}
    </>
  );
}
